<template>
    <v-container fluid>
        <v-card>
            <v-card-title>
                <v-icon left>mdi-cog</v-icon>
                Configuración AFIP/ARCA
            </v-card-title>
            
            <v-card-text>
                <v-alert type="info" dense text>
                    Configure los datos de AFIP para habilitar la facturación electrónica.
                    Los certificados se guardarán encriptados en la base de datos.
                </v-alert>
                
                <v-form ref="form" v-model="valid">
                    <v-row>
                        <!-- CUIT -->
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.cuit"
                                label="CUIT *"
                                outlined
                                :rules="[rules.required, rules.cuit]"
                                hint="11 dígitos sin guiones"
                            />
                        </v-col>
                        
                        <!-- Razón Social -->
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="form.razonSocial"
                                label="Razón Social *"
                                outlined
                                :rules="[rules.required]"
                            />
                        </v-col>
                        
                        <!-- Domicilio Fiscal -->
                        <v-col cols="12">
                            <v-text-field
                                v-model="form.domicilioFiscal"
                                label="Domicilio Fiscal *"
                                outlined
                                :rules="[rules.required]"
                            />
                        </v-col>
                        
                        <!-- Condición IVA -->
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="form.condicionIVA"
                                :items="condicionesIVA"
                                label="Condición IVA *"
                                outlined
                                :rules="[rules.required]"
                            />
                        </v-col>
                        
                        <!-- Punto de Venta -->
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model.number="form.puntoVenta"
                                label="Punto de Venta *"
                                outlined
                                type="number"
                                :rules="[rules.required, rules.puntoVenta]"
                                hint="Número de punto de venta autorizado por AFIP"
                            />
                        </v-col>
                        
                        <!-- Ambiente -->
                        <v-col cols="12">
                            <v-radio-group v-model="form.ambiente" row>
                                <v-radio label="Homologación (Testing)" value="HOMOLOGACION" />
                                <v-radio label="Producción" value="PRODUCCION" />
                            </v-radio-group>
                            <v-alert v-if="form.ambiente === 'PRODUCCION'" type="warning" dense>
                                ⚠️ Modo PRODUCCIÓN: Las facturas serán reales y reportadas a AFIP
                            </v-alert>
                        </v-col>
                        
                        <!-- Certificado PEM -->
                        <v-col cols="12" md="6">
                            <v-file-input
                                v-model="certificadoFile"
                                label="Certificado AFIP (.pem o .crt) *"
                                outlined
                                accept=".pem,.crt"
                                :rules="[rules.certificado]"
                                @change="leerCertificado"
                            >
                                <template v-slot:append-outer>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on">mdi-help-circle</v-icon>
                                        </template>
                                        <span>Certificado digital obtenido de AFIP</span>
                                    </v-tooltip>
                                </template>
                            </v-file-input>
                        </v-col>
                        
                        <!-- Clave Privada -->
                        <v-col cols="12" md="6">
                            <v-file-input
                                v-model="claveFile"
                                label="Clave Privada (.key) *"
                                outlined
                                accept=".key"
                                :rules="[rules.clave]"
                                @change="leerClave"
                            >
                                <template v-slot:append-outer>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-icon v-on="on">mdi-help-circle</v-icon>
                                        </template>
                                        <span>Clave privada generada al crear el CSR</span>
                                    </v-tooltip>
                                </template>
                            </v-file-input>
                        </v-col>
                    </v-row>
                </v-form>
                
                <!-- Info de ayuda -->
                <v-expansion-panels class="mt-4">
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <v-icon left>mdi-information</v-icon>
                            ¿Cómo obtener los certificados AFIP?
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <ol>
                                <li>Generar clave privada y CSR con OpenSSL</li>
                                <li>Ir a AFIP: <a href="https://www.afip.gob.ar/ws/WSASS/homologacion.aspx" target="_blank">Homologación</a></li>
                                <li>Subir el CSR y descargar el certificado</li>
                                <li>Cargar ambos archivos aquí</li>
                            </ol>
                            <v-alert type="info" dense class="mt-2">
                                Documentación completa en el backend: <code>README-ARCA-SDK.md</code>
                            </v-alert>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card-text>
            
            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="primary"
                    large
                    :loading="loading"
                    :disabled="!valid || !form.certificadoPEM || !form.clavePrivadaPEM"
                    @click="guardar"
                >
                    <v-icon left>mdi-content-save</v-icon>
                    Guardar Configuración
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
// Nota: Este componente debería ir en el admin-panel, no en el frontend normal
// Usar el CompanyProvider del admin-panel para actualizar la configuración

export default {
    name: 'ConfiguracionAfipPage',
    
    data() {
        return {
            valid: false,
            loading: false,
            certificadoFile: null,
            claveFile: null,
            
            form: {
                cuit: '',
                razonSocial: '',
                domicilioFiscal: '',
                condicionIVA: 'RESPONSABLE_INSCRIPTO',
                puntoVenta: 1,
                ambiente: 'HOMOLOGACION',
                certificadoPEM: null,
                clavePrivadaPEM: null
            },
            
            condicionesIVA: [
                { text: 'Responsable Inscripto', value: 'RESPONSABLE_INSCRIPTO' },
                { text: 'Monotributista', value: 'MONOTRIBUTISTA' },
                { text: 'Exento', value: 'EXENTO' },
            ],
            
            rules: {
                required: v => !!v || 'Campo requerido',
                cuit: v => {
                    if (!v) return true
                    const clean = v.replace(/[-\s]/g, '')
                    return clean.length === 11 || 'CUIT debe tener 11 dígitos'
                },
                puntoVenta: v => {
                    if (!v) return true
                    return (v >= 1 && v <= 9999) || 'Punto de venta entre 1 y 9999'
                },
                certificado: v => {
                    if (!this.form.certificadoPEM && !v) return 'Certificado requerido'
                    return true
                },
                clave: v => {
                    if (!this.form.clavePrivadaPEM && !v) return 'Clave privada requerida'
                    return true
                }
            }
        }
    },
    
    methods: {
        async leerCertificado(file) {
            if (!file) return
            
            try {
                const text = await this.leerArchivo(file)
                this.form.certificadoPEM = text
            } catch (error) {
                this.$snackbar.showMessage({
                    color: 'error',
                    message: 'Error leyendo certificado: ' + error.message
                })
            }
        },
        
        async leerClave(file) {
            if (!file) return
            
            try {
                const text = await this.leerArchivo(file)
                this.form.clavePrivadaPEM = text
            } catch (error) {
                this.$snackbar.showMessage({
                    color: 'error',
                    message: 'Error leyendo clave privada: ' + error.message
                })
            }
        },
        
        leerArchivo(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target.result)
                reader.onerror = e => reject(e)
                reader.readAsText(file)
            })
        },
        
        async guardar() {
            if (!this.$refs.form.validate()) return
            
            this.loading = true
            
            try {
                // TODO: Usar CompanyProvider del admin-panel
                // await CompanyProvider.updateCompany(companyId, {
                //     cuit: this.form.cuit,
                //     razonSocial: this.form.razonSocial,
                //     afipDomicilioFiscal: this.form.domicilioFiscal,
                //     afipCondicionIVA: this.form.condicionIVA,
                //     afipPuntoVenta: this.form.puntoVenta,
                //     afipAmbiente: this.form.ambiente,
                //     afipCertificadoPEM: this.form.certificadoPEM,
                //     afipClavePrivadaPEM: this.form.clavePrivadaPEM,
                //     afipActivo: true
                // })
                
                this.$snackbar.showMessage({
                    color: 'success',
                    message: 'Configuración AFIP guardada correctamente'
                })
                
                this.$router.push('/') // O donde corresponda
            } catch (error) {
                this.$snackbar.showMessage({
                    color: 'error',
                    message: 'Error guardando configuración: ' + error.message
                })
            } finally {
                this.loading = false
            }
        }
    }
}
</script>
